import {toast} from 'react-toastify';
import {enabledPaymentMethod} from 'utils/mock';

export const handleUpdatePaymentProviderSetting = (
  mutation,
  values,
  provider,
  enableProvider,
  selectedCurrency,
  depositFeeValues,
  withdrawalFeeValues,
  depositDiscount,
  withdrawalDiscount,
  onSuccess,
) => {
  // Get all unique fee types from enabledPaymentMethod
  const feeTypes = [...new Set(enabledPaymentMethod.map(data => data.key))];

  const feeSchedule = generateFeeSchedule(
    feeTypes,
    depositFeeValues,
    withdrawalFeeValues,
  );

  const discountFeeSchedule = generateFeeSchedule(
    feeTypes,
    depositDiscount,
    withdrawalDiscount,
  );

  // Returned so callers (e.g. the confirm modal) can await the round trip.
  return mutation({
    variables: {
      input: [
        {
          provider: provider,
          currency: selectedCurrency,
          enabled: enableProvider,
          feeSchedule: feeSchedule,
          discount: discountFeeSchedule,
          deposit: {
            enabled: values.allowedDeposit,
            maxDepositAmount: Number(values.maxDepositAmount),
            minDepositAmount: Number(values.minDepositAmount),
            maxNumberOfDepositPerDay: Number(values.depositADay),
            limit: {
              daily: Number(values.dailyLimit),
              weekly: Number(values.weeklyLimit),
              monthly: Number(values.monthlyLimit),
            },
          },
          withdrawal: {
            enabled: values.allowedWithdrawal,
            autoEnabled: values.autoEnabled || false,
            maxWithdrawalAmount: Number(values.maxWithdrawalAmount),
            minWithdrawalAmount: Number(values.minWithdrawalAmount),
            maxNumberOfWithdrawalPerDay: Number(values.withdrawalDepositADay),
            limit: {
              daily: Number(values.dailyWithdrawalLimit),
              weekly: Number(values.weeklyWithdrawalLimit),
              monthly: Number(values.monthlyWithdrawalLimit),
              ...(values?.dailyAuto !== undefined && {
                dailyAuto: Number(values.dailyAuto),
              }),
              ...(values?.weeklyAuto !== undefined && {
                weeklyAuto: Number(values.weeklyAuto),
              }),
              ...(values?.monthlyAuto !== undefined && {
                monthlyAuto: Number(values.monthlyAuto),
              }),
              ...(values?.dailyAutoPro !== undefined && {
                dailyAutoPro: Number(values.dailyAutoPro),
              }),
              ...(values?.weeklyAutoPro !== undefined && {
                weeklyAutoPro: Number(values.weeklyAutoPro),
              }),
              ...(values?.monthlyAutoPro !== undefined && {
                monthlyAutoPro: Number(values.monthlyAutoPro),
              }),
            },
          },
        },
      ],
    },
  })
    .then(({data: {updatePaymentProviderCurrencySettings}}) => {
      if (updatePaymentProviderCurrencySettings.__typename === 'Error') {
        toast.error(updatePaymentProviderCurrencySettings.message);
      } else {
        toast.success(`${selectedCurrency} Updated Successfully.`);
        onSuccess?.();
      }
    })
    .catch(error => {
      toast.error(error?.message ?? 'Something went wrong, please try again');
    });
};

const generateFeeSchedule = (
  feeTypes,
  depositFeeValues,
  withdrawalFeeValues,
) => {
  return feeTypes
    .map(method => {
      // Filter all deposit and withdrawal fees for the current feeType

      const depositFeesForFeeType = depositFeeValues?.filter(
        data => data?.paymentMethodType === method,
      );
      const withdrawalFeesForFeeType = withdrawalFeeValues?.filter(
        data => data?.paymentMethodType === method,
      );

      // Combine both deposit and withdrawal fees
      const combinedFees = [
        ...depositFeesForFeeType?.map(data => ({
          fee: [
            {
              fixedFeeAmount: Number(data?.fixedFee) || 0,
              variableFeeAmount: Number(data?.variableFee) || 0,
              feeType: data?.feeType,
            },
          ],
          paymentMethodType: data?.paymentMethodType,
          operationType: 'DEPOSIT',
        })),
        ...withdrawalFeesForFeeType.map(data => ({
          fee: [
            {
              fixedFeeAmount: Number(data?.fixedFee) || 0,
              variableFeeAmount: Number(data?.variableFee) || 0,
              feeType: data?.feeType,
            },
          ],
          paymentMethodType: data?.paymentMethodType,
          operationType: 'WITHDRAWAL',
        })),
      ];

      if (combinedFees.length === 0) return null;

      const feeByOperationType = combinedFees.reduce((acc, fee) => {
        const {operationType, fee: feeDetails} = fee;
        if (!acc[operationType]) acc[operationType] = [];
        acc[operationType].push(...feeDetails);
        return acc;
      }, {});

      return Object.entries(feeByOperationType).map(
        ([operationType, fees]) => ({
          fee: fees, // List of fees for the operation type
          paymentMethodType: method,
          operationType,
        }),
      );
    })
    .filter(Boolean) // Remove nulls
    .flat(); // Flatten array
};
